import React, { useEffect, useState } from 'react'
import {
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Form,
  FormGroup,
  FormFeedback
} from 'reactstrap'
import { IService } from './index'
import { toast } from 'react-toastify'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { isEqual } from 'lodash'

interface IProps {
  serviceRecord: IService | undefined
  isOpen: boolean
  editMode: boolean
  setOpenPopup: (arg: boolean) => void
  handleCreateOrUpdateService: (arg: any) => Promise<string>
  canEdit: boolean
}

const initService: IService = {
  name: '',
  label: '',
  description: '',
  price: 100,
  isActive: true,
  printQrUrl: ''
}

const ServiceModal: React.FC<IProps> = ({
  serviceRecord,
  isOpen,
  editMode,
  handleCreateOrUpdateService,
  setOpenPopup,
  canEdit
}) => {
  const [serviceState, setServiceState] = useState<IService>(initService)
  const [initServiceState, setInitServiceState] = useState<IService>(initService)
  const [disableSave, setDisableSave] = useState<boolean>(editMode)
  const hasEdit = (editMode && serviceRecord) || false

  const formik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: hasEdit ? serviceRecord?.name : '',
      label: hasEdit ? serviceRecord?.label : '',
      description: hasEdit ? serviceRecord?.description : '',
      price: hasEdit ? serviceRecord?.price : 100,
      printQrUrl: hasEdit ? serviceRecord?.printQrUrl || '' : ''
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Please Enter Name'),
      label: Yup.string().required('Please Enter Label'),
      price: Yup.number().required('Please Enter Price').min(0, 'Price must be positive'),
      printQrUrl: Yup.string().matches(
        /^https?:\/\/\S+$/i,
        {
          message: 'Adres http:// veya https:// ile baslamali (ornek: https://instagram.com/hesap)',
          excludeEmptyString: true
        }
      )
    }),
    onSubmit: () => {
      handleSubmit()
    }
  })

  useEffect(() => {
    if (editMode && serviceRecord) {
      // printQrUrl alani eski kayitlarda yok → '' ile esitle ki Save bosuna acilmasin
      const rec = { ...serviceRecord, printQrUrl: serviceRecord.printQrUrl || '' }
      setServiceState(rec)
      setInitServiceState(rec)
      setDisableSave(true)
    } else {
      setServiceState(initService)
      setDisableSave(false)
    }

    if (isOpen) {
      formik.resetForm()
    }
  }, [isOpen])

  useEffect(() => {
    setDisableSave(isEqual(serviceState, initServiceState))
  }, [serviceState])

  const togglePopup = () => {
    setOpenPopup(!isOpen)
  }

  const handleOnChange = (e: { target: { name: any; value: any } }) => {
    const name = e.target.name
    let value: any = e.target.value
    if (name === 'price') {
      value = value === '' ? '' : Number(value)
    }
    setServiceState({ ...serviceState, [name]: value })
    formik.setFieldValue(name, value)
  }

  const handleChangeActive = (checked: boolean) => {
    setServiceState({ ...serviceState, isActive: checked })
  }

  const handleSubmit = async () => {
    const params: any = {
      name: serviceState.name,
      label: serviceState.label,
      description: serviceState.description,
      price: serviceState.price,
      isActive: serviceState.isActive,
      printQrUrl: (serviceState.printQrUrl || '').trim()
    }
    if (editMode) {
      params._id = serviceState._id
    }
    const message = await handleCreateOrUpdateService(params)
    if (message === 'SUCCESSFULL') {
      toast.success(
        editMode ? 'Updated service successfully' : 'Created new service successfully'
      )
      setOpenPopup(false)
    } else {
      toast.error(message)
    }
  }

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        toggle={togglePopup}
        scrollable={true}
        id='serviceModal'
      >
        <ModalHeader className='modal-title' id='serviceModalTitle' toggle={togglePopup}>
          {editMode ? 'Service Details' : 'Create a new Service'}
        </ModalHeader>
        <ModalBody>
          <Form
            className='needs-validation'
            onSubmit={(e) => {
              e.preventDefault()
              formik.handleSubmit()
              return false
            }}
          >
            <Row>
              <FormGroup>
                <Col md={12} className='mb-3'>
                  <div>
                    <Label htmlFor='name' className='form-label mb-1 fw-semibold'>
                      Name (slug)
                    </Label>
                    <Input
                      type='text'
                      maxLength={50}
                      className='form-control'
                      id='name'
                      name='name'
                      placeholder='e.g. buyulu_selfie'
                      value={serviceState.name}
                      onChange={handleOnChange}
                      invalid={formik.touched.name && formik.errors.name ? true : false}
                    />
                    {formik.touched.name && formik.errors.name ? (
                      <FormFeedback type='invalid'>{formik.errors.name}</FormFeedback>
                    ) : null}
                  </div>
                </Col>
              </FormGroup>
              <FormGroup>
                <Col md={12} className='mb-3'>
                  <div>
                    <Label htmlFor='label' className='form-label mb-1 fw-semibold'>
                      Label
                    </Label>
                    <Input
                      type='text'
                      maxLength={50}
                      className='form-control'
                      id='label'
                      name='label'
                      placeholder='Display name'
                      value={serviceState.label}
                      onChange={handleOnChange}
                      invalid={formik.touched.label && formik.errors.label ? true : false}
                    />
                    {formik.touched.label && formik.errors.label ? (
                      <FormFeedback type='invalid'>{formik.errors.label}</FormFeedback>
                    ) : null}
                  </div>
                </Col>
              </FormGroup>
              <FormGroup>
                <Col md={12} className='mb-3'>
                  <div>
                    <Label htmlFor='price' className='form-label mb-1 fw-semibold'>
                      Price
                    </Label>
                    <Input
                      type='number'
                      min={0}
                      className='form-control'
                      id='price'
                      name='price'
                      placeholder='Price'
                      value={serviceState.price}
                      onChange={handleOnChange}
                      invalid={formik.touched.price && formik.errors.price ? true : false}
                    />
                    {formik.touched.price && formik.errors.price ? (
                      <FormFeedback type='invalid'>{formik.errors.price}</FormFeedback>
                    ) : null}
                  </div>
                </Col>
              </FormGroup>
              <FormGroup>
                <Col md={12} className='mb-3'>
                  <div>
                    <Label htmlFor='printQrUrl' className='form-label mb-1 fw-semibold'>
                      Print QR URL
                    </Label>
                    <Input
                      type='url'
                      maxLength={300}
                      className='form-control'
                      id='printQrUrl'
                      name='printQrUrl'
                      placeholder='https://...'
                      value={serviceState.printQrUrl || ''}
                      onChange={handleOnChange}
                      invalid={formik.errors.printQrUrl ? true : false}
                    />
                    {formik.errors.printQrUrl ? (
                      <FormFeedback type='invalid'>{formik.errors.printQrUrl}</FormFeedback>
                    ) : null}
                    <small className='text-muted'>
                      Biyometrik baskida karekod musterinin KENDI fotografini acar (sifreli, 1 saat
                      sonra silinir). Bu adres yalniz fotograf baglantisi olusturulamazsa (kabinde
                      internet yoksa) kullanilir. Bos ise o durumda karekod basilmaz.
                    </small>
                  </div>
                </Col>
              </FormGroup>
              <FormGroup>
                <Col md={12} className='mb-3'>
                  <div className='form-check'>
                    <Input
                      className='form-check-input'
                      type='checkbox'
                      id='service-active'
                      checked={serviceState.isActive}
                      onChange={(e) => handleChangeActive(e.target.checked)}
                    />
                    <Label className='form-check-label fw-medium' htmlFor='service-active'>
                      Active
                    </Label>
                  </div>
                </Col>
              </FormGroup>
            </Row>
            <div className='modal-footer'>
              <Button color='light' onClick={() => setOpenPopup(false)}>
                Close
              </Button>
              {canEdit && (
                <Button color='primary' type='submit' disabled={disableSave}>
                  Save
                </Button>
              )}
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}
export default ServiceModal
